import { create, useStore } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { BlogCategory } from "../types/category"
import type { ICheckout } from "../types/course/checkout"
import { initialCheckout } from "../types/course/checkout"
import type { IMyCourse } from "../types/course/my"
import { type IFooter, initialFooterData } from "../types/footer"
import type { CourseDetail } from "../types/course/detail"
import type { IOrder } from "../types/order"
import { type IUser, initialUserProfile } from "../types/user"
import { useAffiliateStore } from "./affiliateStore"

interface BearState {
	blogCategory: Array<BlogCategory>
	carts: Array<{ course_id: number }>
	courseCategory: Array<CourseDetail>
	myCourses: Array<IMyCourse>
	order: Array<IOrder>
	pendingCourse: number
	pendingApp: number
	productTag: Array<{ category: string }>
	profileUser: IUser
	footerMenu: IFooter
	lastCheckout: ICheckout
	logout: () => void
	setBlogCategory: (data: Array<BlogCategory>) => void
	setCarts: (carts: Array<{ course_id: number }>) => void
	setCourseCategory: (categories: Array<CourseDetail>) => void
	setMyCourses: (courses: Array<IMyCourse>) => void
	setProductTag: (data: Array<{ category: string }>) => void
	setProfileUser: (data: IUser) => void
	setFooterMenu: (data: IFooter) => void
	setOrders: (orders: Array<IOrder>) => void
	setLastCheckout: (data: ICheckout) => void
	setPendingCourse: (pendingCourse: number) => void
	setPendingApp: (pendingApp: number) => void
	reset: () => void
}
const defaultState: BearState = {
	blogCategory: [],
	order: [],
	carts: [],
	courseCategory: [],
	myCourses: [],
	productTag: [],
	footerMenu: initialFooterData,
	profileUser: initialUserProfile,
	lastCheckout: initialCheckout,
	pendingCourse: 0,
	pendingApp: 0,
	setBlogCategory: () => {},
	setCarts: () => {},
	setCourseCategory: () => {},
	setMyCourses: () => {},
	setProductTag: () => {},
	setProfileUser: () => {},
	setFooterMenu: () => {},
	setOrders: () => {},
	setLastCheckout: () => {},
	setPendingCourse: () => {},
	setPendingApp: () => {},
	reset: () => {},
	logout: () => {},
}

export const useBearStore = create<BearState>()(
	persist(
		(set, _) => ({
			blogCategory: [],
			order: [],
			carts: [],
			courseCategory: [],
			myCourses: [],
			productTag: [],
			pendingCourse: 0,
			pendingApp: 0,
			footerMenu: initialFooterData,
			lastCheckout: initialCheckout,
			profileUser: initialUserProfile,
			setBlogCategory: (data) => set({ blogCategory: data }),
			setCarts: (carts) => set({ carts: carts }),
			setCourseCategory: (categories) => set({ courseCategory: categories }),
			setMyCourses: (courses) => set({ myCourses: courses }),
			setOrders: (orders) => set({ order: orders }),
			setProductTag: (data) => set({ productTag: data }),
			setProfileUser: (data) => set({ profileUser: data }),
			setFooterMenu: (data) => set({ footerMenu: data }),
			setLastCheckout: (data) => set({ lastCheckout: data }),
			setPendingCourse: (pendingCourse) =>
				set({ pendingCourse: pendingCourse }),
			setPendingApp: (pendingApp) =>
				set({ pendingApp: pendingApp }),
			logout: () => {
				useBearStore.persist.clearStorage()
				set(defaultState)
				// Clear affiliate data when logout
				const affiliateStore = useAffiliateStore.getState()
				affiliateStore.clearAffiliateStatus()
				affiliateStore.clearAffiliateOrders()
			},
			reset: () => {
				useBearStore.persist.clearStorage()
				set(defaultState)
			},
		}),
		{
			name: "gpems-new-storage",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				lastCheckout: state.lastCheckout,
				profileUser: state.profileUser,
			}),
			merge: (persistedState: unknown, currentState: BearState) => {
				const typedPersistedState = persistedState as Partial<BearState>
				if (typedPersistedState.profileUser?.id !== 0) {
					return {
						...currentState,
						...typedPersistedState,
					}
				}
				return currentState
			},
		},
	),
)
